// RPTLAYOUT1-F1-S3 (docs/build-briefs/RPTLAYOUT1-F1-S3-guided-report-builder.md): the pure model of a saved custom report in the
// results screen. It orders and words what the server sent. It computes no figure: every cell, total and change is the server's.
import type { CustomReportCell, CustomReportResultDto } from "./api.ts";
import { compositePresentation } from "./compositeReportModel.ts";
import {
  NOT_AVAILABLE,
  compareCells,
  compareHeads,
  dateSpanText,
  formatCell,
  isFigure,
  locationsText,
  rowCountText,
  totalsRow,
  truncationText,
  type ChangeTone,
  CR_EXPORT_ROW_CAP,
} from "./customReportModel.ts";
import type { FlowKind } from "./reportBuilderFlowModel.ts";

/** Who may export and schedule a saved report, as permission ids (the server re-checks both). */
export const RESULT_PERMISSIONS: Readonly<Record<FlowKind, { export: readonly string[]; schedule: readonly string[] }>> = {
  location: { export: ["report.custom.view", "report.export"], schedule: ["report.custom.build"] },
  network: { export: ["network.reports.run"], schedule: ["network.reports.build"] },
};

export function mayExportSaved(kind: FlowKind, held: (permission: string) => boolean): boolean {
  return RESULT_PERMISSIONS[kind].export.every(held);
}
export function maySchedule(kind: FlowKind, held: (permission: string) => boolean): boolean {
  return RESULT_PERMISSIONS[kind].schedule.every(held);
}

export interface ResultColumn {
  key: string;
  label: string;
  figure: boolean;
}
export interface ResultCell {
  text: string;
  /** The raw value to order by; null sorts last. */
  value: CustomReportCell;
  /** A change cell carries a tone for its arrow and color. */
  tone?: ChangeTone;
}
export interface ResultFigure {
  label: string;
  text: string;
  change: { text: string; tone: ChangeTone } | null;
}
export interface ResultView {
  columns: ResultColumn[];
  rows: ResultCell[][];
  total: ResultCell[] | null;
  figures: ResultFigure[];
  count: string;
  note: string;
  dates: string;
  locations: string;
}

/** The result as the screen draws it. `labels` are the catalog's enum words by field, when the catalog loaded. */
export function resultView(
  raw: CustomReportResultDto,
  labels: Readonly<Record<string, Readonly<Record<string, string>>>> = {},
  permitted = raw.meta.locations.length,
): ResultView {
  const result = compositePresentation(raw);
  const heads = compareHeads(result);
  const columns: ResultColumn[] = [
    ...result.columns.map((column) => ({ key: column.key, label: column.label, figure: isFigure(column) })),
    ...heads.map((head) => ({ key: head.key, label: head.label, figure: true })),
  ];
  const wordsFor = (at: number, value: CustomReportCell): string => {
    const column = result.columns[at];
    return column === undefined ? NOT_AVAILABLE : formatCell(value, column.type, labels[column.field]);
  };
  const rows = result.rows.map((row, rowAt) => [
    ...row.map((value, at) => ({ text: wordsFor(at, value), value })),
    ...compareCells(result, rowAt).map((cell) => ({ text: cell.text, value: null as CustomReportCell, tone: cell.tone })),
  ]);
  const totals = totalsRow(result);
  const total =
    totals === null
      ? null
      : [
          ...totals.map((text) => ({ text, value: null as CustomReportCell })),
          ...compareCells(result, "totals").map((cell) => ({ text: cell.text, value: null as CustomReportCell, tone: cell.tone })),
        ];
  const figures: ResultFigure[] = [];
  if (result.totals !== null) {
    const compare = compareCells(result, "totals");
    let measure = 0;
    result.columns.forEach((column) => {
      if (column.kind !== "measure") return;
      const change = compare[measure * 2 + 1];
      figures.push({
        label: column.label,
        text: formatCell(result.totals?.[measure] ?? null, column.type),
        change: change === undefined || result.compare === undefined ? null : { text: change.text, tone: change.tone },
      });
      measure += 1;
    });
  }
  return {
    columns,
    rows,
    total,
    figures,
    count: rowCountText(result),
    note: truncationText(result, CR_EXPORT_ROW_CAP),
    dates: dateSpanText(result.meta.from, result.meta.to),
    locations: locationsText(result.meta.locations, permitted),
  };
}

export interface ResultSort {
  column: number;
  dir: "asc" | "desc";
}

/** A first tap sorts figures high to low and words A to Z; the same header again flips. */
export function nextResultSort(view: ResultView, current: ResultSort | null, column: number): ResultSort {
  if (current?.column === column) return { column, dir: current.dir === "asc" ? "desc" : "asc" };
  return { column, dir: view.columns[column]?.figure === true ? "desc" : "asc" };
}

/** Rows ordered by one column. A missing value sorts last either way, ties keep the server's order, the total row is not a row. */
export function sortedRows(view: ResultView, sort: ResultSort | null): ResultCell[][] {
  if (sort === null) return view.rows;
  const factor = sort.dir === "asc" ? 1 : -1;
  return view.rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = a.row[sort.column]?.value ?? null;
      const right = b.row[sort.column]?.value ?? null;
      const leftMissing = left === null;
      const rightMissing = right === null;
      if (leftMissing || rightMissing) return leftMissing === rightMissing ? a.index - b.index : leftMissing ? 1 : -1;
      const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right), "en-US", { numeric: true, sensitivity: "base" });
      return order === 0 ? a.index - b.index : order * factor;
    })
    .map((entry) => entry.row);
}

/** The version a saved report is asked for. The network route takes it in the query, the location route in the body. */
export function savedRunRequest(kind: FlowKind, prefix: string, id: string, version: number | undefined): { path: string; body: Record<string, unknown> } {
  const base = `${prefix}/saved/${encodeURIComponent(id)}/run`;
  if (kind === "network") return { path: version === undefined ? base : `${base}?version=${version}`, body: {} };
  return { path: base, body: version === undefined ? {} : { version } };
}
export function savedExportRequest(
  kind: FlowKind,
  prefix: string,
  id: string,
  version: number | undefined,
  format: "csv" | "pdf" | "sheets" = "csv",
): { path: string; body: Record<string, unknown> } {
  const base = `${prefix}/saved/${encodeURIComponent(id)}/export`;
  if (kind === "network") return { path: `${base}?format=${format}${version === undefined ? "" : `&version=${version}`}`, body: {} };
  return { path: `${base}?format=${format}`, body: version === undefined ? {} : { version } };
}

/** The label the Recently run list keeps: the dates, in at most 80 characters. */
export function runMark(view: ResultView): string {
  return view.dates.slice(0, 80);
}
